// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
// Centre de tâches — « ce que l'application attend de moi », au même endroit
// dans les trois modules (entre le profil et les paramètres).
//
// Rien n'allait chercher l'utilisateur : un accusé de lecture non donné, une
// action en retard, une maintenance due vivaient chacun sur un écran différent,
// et il fallait les visiter pour découvrir ce qu'on attendait de soi.
//
// Deux partis pris structurants :
//
//  · Les tâches sont DÉRIVÉES de l'état réel, jamais stockées ni « marquées
//    lues ». Une tâche disparaît parce que le fait qui la produisait a disparu.
//    Un centre de notifications à marquer soi-même dérive de la réalité en
//    quelques jours, et un compteur qui ment cesse d'être regardé.
//  · Le badge compte TOUT ce qui reste à faire, et vire au rouge dès qu'une
//    échéance est dépassée. Il ne comptait d'abord que le retard, pour éviter le
//    badge mort qui affiche « 47 » en permanence — mais la plupart des tâches
//    n'ont aucune échéance (un message à lire, une demande de compte à statuer) :
//    elles n'étaient donc JAMAIS notifiées, et une tâche non faite qui ne se
//    signale pas est une tâche oubliée. La couleur porte désormais l'urgence, le
//    nombre porte le reste-à-faire.
import React from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { cheminTache } from './cheminTache';

export interface Tache {
  famille: string;
  libelle: string;
  objet: string;
  detail?: string;
  module: string;
  vue: string;
  /** Référence de la pièce concernée, quand elle en a une : c'est elle qui
   *  permet d'ouvrir LA demande plutôt que la liste qui la contient. */
  ref?: string;
  echeance?: string | null;
  retard?: boolean;
  /** Clé de rangement, CALCULÉE PAR LE MOTEUR et servie avec la tâche.
   *  La recomposer ici en aurait fait une troisième copie de la règle (et de
   *  son séparateur invisible) : au premier champ ajouté, les deux se seraient
   *  tues en silence et la croix n'aurait plus rien trouvé à ranger. */
  cle?: string;
  /** Ce que la croix fera : `durable` (les quatre familles qui n'attendent
   *  aucun geste) ou `jour` (toutes les autres — la notification revient
   *  demain si le travail n'est pas fait). */
  portee?: 'durable' | 'jour';
}

/** Une famille réglable, telle que le moteur la sert. Le catalogue n'est PAS
 *  recopié ici : deux listes pour une même vérité, et l'une des deux ment. */
export interface FamilleReglable {
  code: string; label: string; quoi: string;
  nature: 'opposable' | 'informative'; consequence: string;
}

const ICONE: Record<string, string> = {
  accuse: 'check', action: 'alert', signature: 'doc',
  // Non-conformité DÉCLARÉE, en attente de prise en charge (26/08/2026) —
  // servie au RQ et à son suppléant seulement. Même triangle que l'action
  // corrective : ce sont les deux faces du même écart, et le libellé les
  // distingue mieux qu'une seconde forme ne le ferait.
  nc: 'alert',
  maintenance: 'wrench', cqi: 'chart', demande: 'mail',
  // Maintenance PLANIFIÉE à acquitter (lot N4) — famille distincte de
  // `maintenance`, qui porte l'échéance générique de l'appareil : l'une se
  // coche acte par acte, l'autre se solde par une intervention.
  'maintenance-plan': 'wrench',
  // Gel de l'applicabilité : le cadenas dit « figé », pas « interdit » — le
  // document reste lisible, c'est son évolution qui est suspendue.
  gel: 'lock',
  // « Votre planning du X a été modifié » (24/08/2026) — la personne planifiée
  // est prévenue ici, au lieu de découvrir la case changée le jour même.
  planning: 'calendar',
  // « Vous êtes concerné(e) » (24/08/2026) — tout acte du journal CIBLÉ sur
  // quelqu'un (habilitation, absence décidée, contrat, mission, action CAPA,
  // pièce consultée…) prévient l'intéressé ici. La cloche, littéralement.
  concerne: 'bell',
  /* Fiches du catalogue validées, à diffuser (07/09/2026). */
  diffusion: 'doc',
};

const fmt = (iso?: string | null) => {
  if (!iso) return null;
  const p = String(iso).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : String(iso);
};

/** Groupe par urgence, pas par famille : ce qu'on veut savoir, c'est quoi
 *  maintenant — la taxonomie ne sert qu'à l'icône. */
function grouper(toutes: Tache[]) {
  const auj = new Date().toISOString().slice(0, 10);
  const dans7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  /* ⚠️ Les notifications qui n'attendent aucun geste (portée `durable`, servie
     par le moteur — `FAMILLES_RANGEABLES`) forment leur PROPRE section : c'est
     au-dessus d'elles, et d'elles seules, que vit « Marquer comme lues ». Le
     bouton en tête du panneau laissait croire qu'il rangeait toute la liste. */
  const infos = toutes.filter((t) => t.portee === 'durable');
  const taches = toutes.filter((t) => t.portee !== 'durable');
  const retard = taches.filter((t) => t.retard);
  const semaine = taches.filter((t) => !t.retard && t.echeance && String(t.echeance).slice(0, 10) <= dans7 && String(t.echeance).slice(0, 10) >= auj);
  const reste = taches.filter((t) => !retard.includes(t) && !semaine.includes(t));
  return [
    { cle: 'retard', titre: 'En retard', items: retard, couleur: 'var(--red-600)' },
    { cle: 'semaine', titre: 'Cette semaine', items: semaine, couleur: 'var(--amber-600)' },
    { cle: 'suite', titre: 'À venir', items: reste, couleur: 'var(--text-muted)' },
    { cle: 'infos', titre: 'À titre d’information', items: infos, couleur: 'var(--text-muted)' },
  ].filter((g) => g.items.length);
}

export function CentreTaches({ urls, moduleCourant }: {
  /** URL de base de chaque module, pour ouvrir la tâche là où elle vit. */
  urls: { site: string; manuel: string; ged: string };
  moduleCourant: 'site' | 'manuel' | 'ged';
}) {
  const [taches, setTaches] = React.useState<Tache[] | null>(null);
  const [erreur, setErreur] = React.useState(false);
  const [ouvert, setOuvert] = React.useState(false);
  const [rangement, setRangement] = React.useState(false);
  /* ── CE QUI NE NOTIFIE PLUS (RQ, 06/10/2026) ─────────────────────────────
     « Où est-ce que je peux désactiver les notifications liées aux
       maintenances ? »  Nulle part, jusqu'ici.
     ⚠️ `masquees` N'EST PAS DÉCORATIF : une cloche qui maigrit sans le dire se
     lit « il n'y a rien à faire », soit le contraire de la vérité. Le compte
     vient du SERVEUR, qui seul sait ce qu'il a retiré. */
  const [masquees, setMasquees] = React.useState(0);
  const [famillesMasquees, setFamillesMasquees] = React.useState<{ code: string; label: string; par: string | null }[]>([]);
  const [reglables, setReglables] = React.useState<FamilleReglable[]>([]);
  const [mesPrefs, setMesPrefs] = React.useState<Record<string, boolean>>({});
  const [eteintesLabo, setEteintesLabo] = React.useState<{ code: string; label: string }[]>([]);
  const [reglagesOuverts, setReglagesOuverts] = React.useState(false);
  const [enregistrement, setEnregistrement] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  /* ⚠️ UN SECOND REF, SUR LE PANNEAU PORTÉ — sans lui, AUCUN bouton du panneau
     ne fonctionne (signalement d'Inès, 07/10/2026 : « rien ne se passe, juste
     la liste disparaît »).
     Le panneau est rendu par `createPortal` sur `document.body` : il n'est donc
     PAS un descendant DOM de `ref`, qui n'entoure que la cloche. Le détecteur
     de clic extérieur jugeait « dehors » tout clic DANS le panneau, le fermait
     au `mousedown`, et React le démontait avant que le `click` n'atteigne sa
     cible. La croix, la roue, « marquer comme lues » ET le clic sur une tâche
     étaient tous morts — un défaut parfaitement muet : le panneau se ferme,
     geste qu'on interprète comme « j'ai cliqué à côté ».
     ⚠️ `createPortal` propage bien les événements REACT à travers l'arbre React,
     mais ce détecteur-ci écoute le DOM natif (`document.addEventListener`) :
     c'est la containment DOM qui décide, et elle ignore le portail. */
  const refPanneau = React.useRef<HTMLDivElement | null>(null);

  const charger = React.useCallback(async () => {
    try {
      // TOUJOURS en relatif : chaque module expose sa propre route `/api/taches`
      // qui proxifie vers le moteur. Viser l'URL du module Qualité aurait produit
      // une requête inter-origines, bloquée par CORS — donc un centre vide sur
      // deux modules sur trois, sans que rien ne l'explique.
      const r = await fetch('/api/taches', { credentials: 'include' });
      if (!r.ok) { setErreur(true); setTaches([]); return; }
      const j = await r.json();
      setErreur(false);
      setTaches(j?.taches || []);
      setMasquees(Number(j?.masquees) || 0);
      setFamillesMasquees(j?.famillesMasquees || []);
      setReglables(j?.famillesReglables || []);
      setMesPrefs((j?.mesPreferences as Record<string, boolean>) || {});
      setEteintesLabo(j?.eteintesParLeLabo || []);
    } catch {
      // On ne se tait pas : un centre de tâches vide parce que le serveur n'a
      // pas répondu se lit « rien à faire », ce qui est le pire des messages.
      setErreur(true);
      setTaches([]);
      /* ⚠️ On ne remet PAS `masquees` à zéro : on n'a rien lu, donc on ne sait
         rien. Afficher « 0 masquée » après un échec serait une affirmation
         fausse — exactement le défaut que le bandeau d'erreur existe pour
         éviter juste à côté. */
    }
  }, []);

  /**
   * CE QUI ME NOTIFIE — préférence personnelle, pour MA cloche seulement.
   *
   * ⚠️ ELLE NE RETIRE RIEN À PERSONNE D'AUTRE. C'est toute la différence avec
   * le réglage du laboratoire (Paramétrage → Alertes), qui vaut pour tous et
   * qui BORNE celle-ci : une famille éteinte là-bas n'est même pas proposée
   * ici, parce qu'une case qu'on coche sans effet est un mensonge d'interface.
   *
   * ⚠️ ON RELIT APRÈS COUP plutôt que de retirer les lignes localement : c'est
   * le serveur qui décide ce qu'il garde, et une liste bricolée ici finirait
   * par diverger de ce qu'il sert vraiment.
   */
  const basculerFamille = React.useCallback(async (code: string, visible: boolean) => {
    const suite = { ...mesPrefs };
    /* On n'enregistre QUE les `false` : un `true` est le défaut, et le stocker
       figerait ce défaut pour une famille qui pourrait disparaître du
       catalogue. */
    if (visible) delete suite[code]; else suite[code] = false;
    setMesPrefs(suite);
    setEnregistrement(true);
    try {
      const r = await fetch('/api/taches', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ familles: suite }),
      });
      if (r.ok) await charger(); else setErreur(true);
    } catch { setErreur(true); } finally { setEnregistrement(false); }
  }, [mesPrefs, charger]);

  /**
   * « ELLES ONT ÉTÉ LUES » — ranger les notifications qui n'attendent rien.
   *
   * ⚠️ CE BOUTON NE RANGE PAS TOUT, et son libellé le dit en portant un
   * compte. Une maintenance en retard, une non-conformité, une signature
   * attendue ne se « lisent » pas : elles restent. Ce qui part, ce sont les
   * annonces — « Vous êtes concerné(e) », un document gelé, une composition de
   * poste, un échange décidé. Le serveur tient cette liste
   * (`FAMILLES_RANGEABLES`), l'écran ne fait que l'afficher.
   *
   * ⚠️ ET CE N'EST PAS DÉFINITIF : le moteur enregistre la tâche AVEC
   * l'empreinte de son contenu. Si le fait change — une échéance qui avance, un
   * détail qui se précise — la notification revient. On range ce qu'on a lu,
   * dans l'état où on l'a lu ; on ne fait pas taire un problème.
   */
  const ranger = React.useCallback(async () => {
    setRangement(true);
    try {
      const r = await fetch('/api/taches', { method: 'POST', credentials: 'include' });
      /* On RELIT plutôt que de retirer les lignes localement : c'est le serveur
         qui sait ce qu'il a rangé, et une liste bricolée ici aurait pu masquer
         une tâche qu'il a refusé de ranger. */
      if (r.ok) await charger();
      else setErreur(true);
    } catch {
      setErreur(true);
    } finally {
      setRangement(false);
    }
  }, [charger]);

  /**
   * LA CROIX — se débarrasser d'UNE notification (RQ, 06/10/2026).
   *
   * ⚠️ ELLE NE FAIT PAS TAIRE UN PROBLÈME. Hors des quatre familles qui
   * n'attendent aucun geste, le rangement vaut POUR LA JOURNÉE : une
   * maintenance due revient demain si elle n'est pas faite. C'est ce qui
   * distingue « je m'en débarrasse » de « je ne le verrai plus jamais » — et
   * pour une échéance opposable, seule la première est acceptable.
   *
   * ⚠️ ON RELIT APRÈS COUP. Retirer la ligne localement aurait affiché une
   * cloche que le serveur ne confirme pas : si le rangement échoue, on croirait
   * la notification partie alors qu'elle revient au prochain chargement.
   */
  const rangerUne = React.useCallback(async (t: Tache) => {
    if (!t.cle) return;
    setRangement(true);
    try {
      const r = await fetch('/api/taches', {
        method: 'DELETE', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cle: t.cle }),
      });
      if (r.ok) await charger(); else setErreur(true);
    } catch { setErreur(true); } finally { setRangement(false); }
  }, [charger]);

  // `.then()` et non `await` : une fonction `async` appelée directement dans
  // l'effet masque le fait que les `setState` suivent le premier await, ce que
  // React 19 signale comme un rendu en cascade.
  React.useEffect(() => {
    let vivant = true;
    void Promise.resolve().then(() => (vivant ? charger() : undefined));
    return () => { vivant = false; };
  }, [charger]);
  React.useEffect(() => {
    if (!ouvert) return;
    const surClic = (e: MouseEvent) => {
      const cible = e.target as Node;
      /* La cloche elle-même : son propre `onClick` bascule l'ouverture. */
      if (ref.current?.contains(cible)) return;
      /* Le panneau porté : on ne ferme pas, sinon le `click` n'arrive jamais. */
      if (refPanneau.current?.contains(cible)) return;
      setOuvert(false);
    };
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', surClic);
    document.addEventListener('keydown', surEchap);
    return () => { document.removeEventListener('mousedown', surClic); document.removeEventListener('keydown', surEchap); };
  }, [ouvert]);

  const liste = taches || [];
  const nbRetard = liste.filter((t) => t.retard).length;
  const groupes = grouper(liste);

  const ouvrir = (t: Tache) => {
    const base = t.module === moduleCourant ? '' : (urls as Record<string, string>)[t.module] || '';
    // On atterrit SUR la pièce, pas sur la liste : cliquer « Message à lire »
    // pour devoir ensuite le retrouver parmi vingt autres n'était pas un lien,
    // c'était une indication.
    /* ⚠️ LA DESTINATION SE CALCULE DANS `cheminTache`, ET PLUS ICI (13/09/2026).
     *
     * Ce qui s'écrivait à cet endroit était `/?view=equip&ref=EQ12` — une forme
     * d'adresse que PLUS RIEN NE LIT depuis le 14/08/2026, date à laquelle la
     * Qualité est passée aux chemins (`/equipements/EQ12`). Le clic partait, le
     * navigateur chargeait la racine, et l'on atterrissait au tableau de bord :
     * depuis le tableau de bord — d'où l'on clique la cloche — il ne se passait
     * donc RIEN. Toutes les tâches de la Qualité étaient concernées, dans les
     * trois groupes (« En retard », « Cette semaine », « À venir »).
     *
     * Un lien mort ne lève aucune erreur : il ouvre autre chose. C'est pourquoi
     * la règle vit désormais dans un module PUR, confronté par un contrôle
     * statique à la table d'adressage de la Qualité et aux vues que le moteur
     * émet réellement. */
    const cible = cheminTache(t, base);
    // `assign()` plutôt que `location.href = …` : même effet, mais c'est un
    // appel de méthode et non l'affectation d'une variable extérieure au
    // composant — ce que React 19 interdit, faute de pouvoir distinguer un
    // gestionnaire d'événement d'un corps de rendu.
    window.location.assign(cible);
  };

  /* Sous 640 px, le panneau cesse d'être un menu ancré à la cloche pour
     devenir une FEUILLE qui prend la largeur de l'écran.
     Ancré, il partait du bord droit et débordait à gauche : le texte des
     tâches — une référence, un titre, une échéance — arrivait tronqué, donc
     illisible (retour Ines du 18/08). Le demander « peu importe la position de
     l'icône » est la bonne formulation : sur un écran étroit, un panneau de
     lecture n'a pas à dépendre d'où se trouve son bouton. */
  const etroit = React.useSyncExternalStore(
    (reagir) => {
      const mq = window.matchMedia('(max-width: 640px)');
      mq.addEventListener('change', reagir);
      return () => mq.removeEventListener('change', reagir);
    },
    () => window.matchMedia('(max-width: 640px)').matches,
    () => false,   // au rendu serveur : la largeur est inconnue, on part du menu
  );

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => { setOuvert((o) => !o); if (!ouvert) void charger(); }}
        aria-expanded={ouvert}
        aria-label={liste.length ? `Mes tâches — ${liste.length} à faire${nbRetard ? `, dont ${nbRetard} en retard` : ''}` : 'Mes tâches — rien à faire'}
        title={liste.length ? `${liste.length} tâche(s) à faire${nbRetard ? ` · ${nbRetard} en retard` : ''}` : 'Mes tâches'}
        className="cb-btn"
        style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, border: '1px solid rgba(255,255,255,0.18)', background: 'transparent', color: '#fff', borderRadius: '50%', cursor: 'pointer' }}>
        <Icon name="bell" size={16} />
        {liste.length > 0 && (
          // Rouge = au moins une échéance dépassée ; ambre = il reste à faire,
          // sans retard. La couleur dit l'urgence, le nombre dit le volume.
          <span aria-hidden style={{ position: 'absolute', top: -3, insetInlineEnd: -3, minWidth: 16, height: 16, padding: '0 4px', borderRadius: 9, background: nbRetard ? 'var(--red-600, #C0392B)' : 'var(--amber-600, #B7791F)', color: '#fff', fontSize: 10, fontWeight: 700, fontFamily: 'var(--font-mono)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{liste.length}</span>
        )}
      </button>

      {/* PORTÉ SUR `document.body` : la barre d'en-tête est un conteneur
          `sticky` porteur d'un `z-index`, donc un contexte d'empilement — un
          enfant `absolute` ou `fixed` y reste enfermé quel que soit son propre
          `z-index`, et se retrouve derrière le contenu. Le portail l'en sort. */}
      {ouvert && typeof document !== 'undefined' && createPortal((
        <div role="dialog" aria-label="Mes tâches" ref={refPanneau}
          style={etroit
            ? {
              // Feuille : collée sous la barre, sur toute la largeur utile.
              position: 'fixed', top: 64, insetInlineStart: 8, insetInlineEnd: 8,
              zIndex: 200, maxHeight: 'calc(100dvh - 96px)', overflow: 'auto',
              background: '#fff', color: 'var(--text-body)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
            }
            : {
              position: 'fixed', top: 64, insetInlineEnd: 16, zIndex: 200,
              width: 'min(400px,92vw)', maxHeight: '70vh', overflow: 'auto',
              background: '#fff', color: 'var(--text-body)',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
            }}>
          <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border-default)', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14, color: 'var(--text-strong)' }}>Mes tâches</strong>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{liste.length} en cours</span>
            <span style={{ flex: 1 }} />
            {/* La roue = RÉGLAGE, jamais une action (iconographie du dépôt).
                Elle vit ICI, dans la cloche, parce que c'est là que naît la
                question « ça m'encombre » — pas dans un écran de paramétrage
                qu'il faudrait savoir chercher. */}
            <button type="button" onClick={() => setReglagesOuverts((v) => !v)}
              aria-expanded={reglagesOuverts}
              aria-label="Choisir ce qui me notifie"
              title="Choisir ce qui me notifie dans cette cloche"
              style={{
                background: 'none', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
                padding: '4px 7px', display: 'inline-flex', alignItems: 'center',
                color: 'var(--text-body)', cursor: 'pointer',
              }}>
              <Icon name="gear" size={13} />
            </button>
          </div>

          {/* ⚠️ CE QUI EST MASQUÉ SE DIT, TOUJOURS. Sans cette ligne, la cloche
              maigrit en silence et se lit « il n'y a rien à faire » — le pire
              message qu'elle puisse porter, et le risque exact qu'on prend en
              rendant les familles décochables. */}
          {masquees > 0 && (
            <button type="button" onClick={() => setReglagesOuverts(true)}
              style={{
                display: 'block', width: '100%', textAlign: 'start', cursor: 'pointer',
                padding: '8px 16px', background: 'var(--amber-50, #FCF8EE)', border: 'none',
                borderBottom: '1px solid var(--border-subtle)', fontFamily: 'var(--font-sans)',
                fontSize: 11.5, color: 'var(--amber-600, #B7791F)', fontWeight: 600,
              }}>
              {masquees} notification{masquees > 1 ? 's' : ''} masquée{masquees > 1 ? 's' : ''} par vos réglages
              {famillesMasquees.length ? ` — ${famillesMasquees.map((f) => f.label).join(', ')}` : ''}
            </button>
          )}

          {reglagesOuverts && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-default)', background: 'var(--gray-50, #F7F9FB)' }}>
              <strong style={{ fontSize: 12.5, color: 'var(--text-strong)', display: 'block', marginBottom: 2 }}>
                Ce qui me notifie
              </strong>
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
                Pour <strong>votre</strong> cloche seulement : vos collègues continuent d’être prévenus.
              </p>
              {!reglables.length && (
                <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: 0 }}>Chargement…</p>
              )}
              {reglables.map((f) => {
                const eteinte = mesPrefs[f.code] === false;
                return (
                  <div key={f.code} style={{ marginBottom: 7 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, cursor: enregistrement ? 'wait' : 'pointer' }}>
                      <input type="checkbox" checked={!eteinte} disabled={enregistrement}
                        onChange={(e) => void basculerFamille(f.code, e.target.checked)}
                        style={{ accentColor: 'var(--color-primary)', marginTop: 2 }} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{f.label}</span>
                        {/* ⚠️ La conséquence n'apparaît QUE si l'on décoche, et
                            seulement pour une échéance opposable : elle reste
                            due, seul le rappel disparaît. C'est la condition
                            posée pour que ces cases existent. */}
                        {eteinte && f.nature === 'opposable' && (
                          <span style={{ display: 'block', fontSize: 11, color: 'var(--amber-600, #B7791F)', lineHeight: 1.45, marginTop: 2 }}>
                            {f.consequence}
                          </span>
                        )}
                      </span>
                    </label>
                  </div>
                );
              })}
              {/* Ce que le laboratoire a éteint est MONTRÉ, pas escamoté : sans
                  cela, on chercherait indéfiniment une case qui n'existe pas. */}
              {eteintesLabo.length > 0 && (
                <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '10px 0 0', lineHeight: 1.5 }}>
                  Éteint par le laboratoire, pour tout le monde :{' '}
                  <strong>{eteintesLabo.map((f) => f.label).join(', ')}</strong>. Seule la responsable
                  qualité peut les rallumer.
                </p>
              )}
            </div>
          )}

          {taches === null && <p style={{ padding: 16, margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</p>}

          {erreur && (
            <p style={{ padding: 16, margin: 0, fontSize: 13, color: 'var(--red-600)' }}>
              Liste indisponible — le serveur n’a pas répondu. Ce n’est pas « rien à faire » : réessayez.
            </p>
          )}

          {/* ⚠️ « RIEN NE VOUS ATTEND » EST UNE AFFIRMATION, et elle devient
              FAUSSE dès qu'un réglage masque quelque chose. C'est le piège que
              rendre les familles décochables introduit : la phrase rassurante
              survit au filtre qui la contredit. Deux états distincts, donc. */}
          {taches !== null && !erreur && liste.length === 0 && masquees === 0 && (
            <p style={{ padding: 16, margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Rien ne vous attend. Les accusés de lecture, actions et signatures qui vous concernent apparaîtront ici.
            </p>
          )}
          {taches !== null && !erreur && liste.length === 0 && masquees > 0 && (
            <p style={{ padding: 16, margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>
              Rien à afficher — mais <strong>{masquees}</strong> notification{masquees > 1 ? 's sont masquées' : ' est masquée'} par vos
              réglages. Ce n’est pas « rien à faire ».
            </p>
          )}

          {groupes.map((g) => (
            <div key={g.cle}>
              <div style={{ padding: '9px 16px 5px', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: g.couleur, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>{g.titre} · {g.items.length}</span>
                <span style={{ flex: 1 }} />
                {/* ⚠️ LE BOUTON NE VIT QU'AU-DESSUS DES NOTIFICATIONS QU'IL
                    RANGE : la section « à titre d'information » n'existe que
                    s'il y en a, donc le bouton ne s'affiche jamais sur une liste
                    de maintenances et de signatures, où il ne ferait rien. */}
                {g.cle === 'infos' && (
                  <button type="button" onClick={() => void ranger()} disabled={rangement}
                    title="Ranger ces notifications, qui n’attendent aucune action de votre part. Une notification revient si le fait qu’elle annonce change."
                    style={{
                      background: 'none', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
                      padding: '3px 9px', fontSize: 11.5, fontWeight: 600, fontFamily: 'var(--font-sans)',
                      textTransform: 'none', letterSpacing: 0,
                      color: 'var(--text-body)', cursor: rangement ? 'not-allowed' : 'pointer',
                      opacity: rangement ? 0.5 : 1,
                    }}>
                    {rangement ? '…' : `Marquer ${g.items.length} comme lue${g.items.length > 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
              {g.items.map((t, i) => (
                /* ⚠️ DEUX BOUTONS CÔTE À CÔTE, dans un conteneur — et non une
                   croix IMBRIQUÉE dans le bouton de la tâche. Un `<button>`
                   dans un `<button>` est invalide : les navigateurs le
                   réparent en les séparant, et le clic sur la croix déclenche
                   alors AUSSI la navigation. On aurait quitté l'écran en
                   croyant ranger. */
                <div key={`${t.famille}-${t.objet}-${i}`}
                  style={{ display: 'flex', alignItems: 'flex-start', borderTop: '1px solid var(--border-subtle)' }}>
                  <button onClick={() => ouvrir(t)}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0, textAlign: 'start', background: 'none', border: 'none', padding: '10px 4px 10px 16px', cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                    <span style={{ color: g.couleur, marginTop: 2 }}><Icon name={ICONE[t.famille] || 'doc'} size={14} /></span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: 'var(--text-strong)' }}>{t.libelle}</span>
                      <span style={{ display: 'block', fontSize: 12, color: 'var(--text-body)', marginTop: 2 }}>
                        <span style={{ fontFamily: 'var(--font-mono)' }}>{t.objet}</span>{t.detail ? ` · ${t.detail}` : ''}
                      </span>
                      {t.echeance && (
                        <span style={{ display: 'block', fontSize: 11, color: t.retard ? 'var(--red-600)' : 'var(--text-muted)', marginTop: 2 }}>
                          {t.retard ? 'échéance dépassée · ' : 'pour le '}{fmt(t.echeance)}
                        </span>
                      )}
                    </span>
                  </button>
                  {t.cle && (
                    /* ⚠️ L'INFOBULLE DIT LA PORTÉE RÉELLE, et ce n'est pas du
                       zèle : une croix qui promet implicitement « pour toujours »
                       sur une maintenance en retard ferait croire qu'on a réglé
                       le problème. Elle NOMME la tâche, parce qu'une croix sans
                       libellé est illisible au lecteur d'écran. */
                    <button type="button" disabled={rangement}
                      onClick={() => void rangerUne(t)}
                      aria-label={`Retirer « ${t.libelle} — ${t.objet} » de mes notifications`}
                      title={t.portee === 'durable'
                        ? 'Retirer cette notification. Elle ne demande aucune action ; elle reviendra seulement si son contenu change.'
                        : 'Retirer cette notification pour aujourd’hui. L’échéance reste due : elle reviendra demain si le travail n’est pas fait.'}
                      style={{
                        background: 'none', border: 'none', padding: '10px 12px 10px 6px',
                        color: 'var(--text-muted)', cursor: rangement ? 'wait' : 'pointer',
                        opacity: rangement ? 0.4 : 0.65, lineHeight: 1,
                        fontSize: 15, fontFamily: 'var(--font-sans)',
                      }}>
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      ), document.body)}
    </div>
  );
}
